/**
 * Availability Helper Utilities
 * Phase 1 Refactor: Extracted from AvailabilityWeeklyDetail.js
 *
 * Pure utility functions for availability management
 */

/**
 * Format hour number to 12-hour format
 * @param {number} hour - Hour in 24-hour format (0-23)
 * @returns {string} Formatted hour string (e.g., "9am", "12pm", "5pm")
 */
export const formatHour = (hour) => {
    if (hour === 12) return "12pm";
    if (hour > 12) return `${hour - 12}pm`;
    return `${hour}am`;
};

/**
 * Convert time string to minutes since midnight
 * @param {string} timeStr - Time string in "HH:MM" format
 * @returns {number} Minutes since midnight
 */
export const timeToMinutes = (timeStr) => {
    const [hours, mins] = timeStr.split(":").map(Number);
    return hours * 60 + mins;
};

/**
 * Convert minutes since midnight to time string
 * @param {number} minutes - Minutes since midnight
 * @returns {string} Time string in "HH:MM" format (e.g., "09:00", "14:30")
 */
export const minutesToTimeString = (minutes) => {
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

/**
 * Check if a time slot falls within lunch break period
 * @param {number} hour - Hour number (9-21)
 * @param {Object} lunchBreak - Lunch break configuration
 * @param {boolean} lunchBreak.enabled - Whether lunch break is enabled
 * @param {string} lunchBreak.startTime - Start time (e.g., "12:00")
 * @param {string} lunchBreak.endTime - End time (e.g., "13:00")
 * @returns {boolean} True if slot is during lunch
 */
export const isLunchHour = (hour, lunchBreak) => {
    if (!lunchBreak || !lunchBreak.enabled || !lunchBreak.startTime || !lunchBreak.endTime) {
        return false;
    }

    const slotStartMinutes = hour * 60;
    const lunchStart = timeToMinutes(lunchBreak.startTime);
    const lunchEnd = timeToMinutes(lunchBreak.endTime);

    // Check if slot start time falls within lunch period
    return slotStartMinutes >= lunchStart && slotStartMinutes < lunchEnd;
};

/**
 * Get color for a travel period (from backend-assigned color)
 * @param {Object} period - Travel period object
 * @param {string} period.color - Backend-assigned color
 * @returns {string} Color hex code
 */
export const getColorForPeriod = (period) => {
    return period.color;
};

/**
 * Generate unique slot key for day/hour combination
 * @param {Object} day - Luxon DateTime object
 * @param {number} hour - Hour number (9-21)
 * @returns {string} Unique slot key (e.g., "2026-01-17_09:00")
 */
export const generateSlotKey = (day, hour) => {
    return `${day.toISODate()}_${String(hour).padStart(2, "0")}:00`;
};

/**
 * Day name abbreviations for calendar display
 */
export const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

/**
 * Generate array of hours for availability slots (9am - 9pm)
 * @returns {number[]} Array of hour numbers [9, 10, 11, ..., 21]
 */
export const generateHours = () => {
    return Array.from({ length: 13 }, (_, i) => i + 9); // 9-21 (9am-9pm)
};

// ============================================================
// Travel-period colour selection — mirrors backend pickTravelColor()
// in backend-R1/src/express/routes/api/v2/availability/shared/helpers.js
//
// LRU rotation + 60° hue-proximity veto. Frontend-only — used by the
// FormInputShowcase simulator. Production travel periods get their
// colour from the backend at creation time; this is for visualisation.
// ============================================================
export const TRAVEL_COLORS = [
    '#d946ef', // Magenta
    '#3b82f6', // Blue
    '#e11d48', // Rose
    '#f59e0b', // Amber
    '#06b6d4', // Cyan
    '#10b981', // Emerald
    '#84cc16', // Lime
];

export const TRAVEL_COLOR_HUES = {
    '#d946ef': 291,
    '#3b82f6': 217,
    '#e11d48': 350,
    '#f59e0b': 38,
    '#06b6d4': 188,
    '#10b981': 160,
    '#84cc16': 84,
};

export const DEFAULT_VETO_DEGREES = 25;
export const DEFAULT_PROXIMITY_DAYS = 30;
export const DEFAULT_LRU_JITTER = 3; // pick randomly among the N oldest non-vetoed candidates

// Family colour names — for UI labels in the simulator.
export const TRAVEL_COLOR_NAMES = {
    '#d946ef': 'Magenta',
    '#3b82f6': 'Blue',
    '#e11d48': 'Rose',
    '#f59e0b': 'Amber',
    '#06b6d4': 'Cyan',
    '#10b981': 'Emerald',
    '#84cc16': 'Lime',
};

// Family scheme presets — colours in the same family veto each other from
// being nearby, regardless of hue distance. Used only by the simulator;
// the production backend uses hue-veto alone.
export const FAMILY_SCHEMES = {
    none: {
        label: 'No families (hue veto only)',
        families: {},
    },
    a: {
        label: 'Scheme A — cool / green / pink + amber solo',
        families: {
            '#06b6d4': 'cool',  // Cyan
            '#3b82f6': 'cool',  // Blue
            '#84cc16': 'green', // Lime
            '#10b981': 'green', // Emerald
            '#d946ef': 'pink',  // Magenta
            '#e11d48': 'pink',  // Rose
            '#f59e0b': null,    // Amber – solo
        },
    },
    b: {
        label: 'Scheme B — cool / green / warm-all',
        families: {
            '#06b6d4': 'cool',
            '#3b82f6': 'cool',
            '#84cc16': 'green',
            '#10b981': 'green',
            '#d946ef': 'warm',
            '#e11d48': 'warm',
            '#f59e0b': 'warm',
        },
    },
};

function hueDistance(a, b) {
    const diff = Math.abs(a - b) % 360;
    return diff > 180 ? 360 - diff : diff;
}

/**
 * Pick a travel-period colour using LRU rotation + hue-proximity veto,
 * optionally augmented with same-family veto.
 *
 * Mirrors the backend implementation when called with default options.
 *
 * @param {Date} newStartDate
 * @param {Date} newEndDate
 * @param {Array<{startDate: Date, endDate: Date, color: string, createdAt?: Date}>} existing
 * @param {object} [options]
 * @param {number} [options.vetoDegrees=25] Hue veto threshold in degrees.
 * @param {number} [options.proximityDays=30] ± window in days for "nearby".
 * @param {object} [options.families={}] Map of hex → family id. Same-family neighbours are vetoed regardless of hue distance.
 * @param {number} [options.lruJitter=3] Pick randomly among the N oldest non-vetoed candidates (1 = strict LRU, breaks cycle pattern when >1).
 * @returns {string} Hex from TRAVEL_COLORS
 */
export function pickTravelColor(newStartDate, newEndDate, existing, options = {}) {
    const vetoDegrees = options.vetoDegrees ?? DEFAULT_VETO_DEGREES;
    const proximityDays = options.proximityDays ?? DEFAULT_PROXIMITY_DAYS;
    const families = options.families || {};
    const lruJitter = Math.max(1, options.lruJitter ?? DEFAULT_LRU_JITTER);
    const proximityMs = proximityDays * 24 * 60 * 60 * 1000;

    const lruScore = Object.fromEntries(TRAVEL_COLORS.map(c => [c, 0]));
    for (const ctx of existing) {
        if (!ctx.color || !(ctx.color in lruScore)) continue;
        const t = ctx.createdAt ? new Date(ctx.createdAt).getTime() : 0;
        if (t > lruScore[ctx.color]) lruScore[ctx.color] = t;
    }
    const lruOrder = [...TRAVEL_COLORS].sort((a, b) => lruScore[a] - lruScore[b]);

    const nearbyColors = new Set();
    for (const ctx of existing) {
        const isProximate =
            ctx.endDate.getTime() >= newStartDate.getTime() - proximityMs &&
            ctx.startDate.getTime() <= newEndDate.getTime() + proximityMs;
        if (isProximate && ctx.color) nearbyColors.add(ctx.color);
    }

    // Families currently in the nearby set — used for family-based veto.
    const nearbyFamilies = new Set();
    for (const c of nearbyColors) {
        const fam = families[c];
        if (fam) nearbyFamilies.add(fam);
    }

    // Walk the LRU list and collect up to `lruJitter` non-vetoed candidates,
    // then pick one at random. With lruJitter=1 this is strict LRU rotation
    // (deterministic cycle). With lruJitter>1 it breaks the cycle while
    // still favouring the older colours.
    const eligible = [];
    for (const candidate of lruOrder) {
        const candHue = TRAVEL_COLOR_HUES[candidate];
        const candFam = families[candidate];
        let allowed = true;

        // Family veto: any colour in the same family as a nearby trip is blocked.
        if (candFam && nearbyFamilies.has(candFam)) {
            allowed = false;
        }

        // Hue veto: blocked if within vetoDegrees of any nearby hue.
        if (allowed) {
            for (const nearby of nearbyColors) {
                if (hueDistance(candHue, TRAVEL_COLOR_HUES[nearby]) < vetoDegrees) {
                    allowed = false;
                    break;
                }
            }
        }

        if (allowed) {
            eligible.push(candidate);
            if (eligible.length >= lruJitter) break;
        }
    }

    if (eligible.length > 0) {
        return eligible[Math.floor(Math.random() * eligible.length)];
    }

    // Hard fallback: max min-hue-distance to any neighbour.
    let bestColor = TRAVEL_COLORS[0];
    let bestDist = -1;
    for (const candidate of TRAVEL_COLORS) {
        const candHue = TRAVEL_COLOR_HUES[candidate];
        let minDist = 180;
        for (const nearby of nearbyColors) {
            const d = hueDistance(candHue, TRAVEL_COLOR_HUES[nearby]);
            if (d < minDist) minDist = d;
        }
        if (minDist > bestDist) {
            bestDist = minDist;
            bestColor = candidate;
        }
    }
    return bestColor;
}
